/**
 * MyHairChair Analytics Service
 * Tracks user behavior for proof of concept and investor data
 * Stores data locally and syncs to backend when available
 */

const ANALYTICS_KEY = 'myhairchair_analytics';
const SESSION_KEY = 'myhairchair_session';

// Generate unique session ID
const generateSessionId = () => {
  return `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Get or create session
const getSession = () => {
  let session = sessionStorage.getItem(SESSION_KEY);
  if (!session) {
    session = generateSessionId();
    sessionStorage.setItem(SESSION_KEY, session);
  }
  return session;
};

// Get stored analytics data
const getStoredAnalytics = () => {
  try {
    const data = localStorage.getItem(ANALYTICS_KEY);
    return data ? JSON.parse(data) : { events: [], metrics: {} };
  } catch {
    return { events: [], metrics: {} };
  }
};

// Save analytics data
const saveAnalytics = (data) => {
  try {
    localStorage.setItem(ANALYTICS_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Failed to save analytics:', e);
  }
};

// Track an event
export const trackEvent = (eventName, eventData = {}) => {
  const analytics = getStoredAnalytics();
  const event = {
    event: eventName,
    data: eventData,
    session_id: getSession(),
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
    user_agent: navigator.userAgent,
    screen_size: `${window.innerWidth}x${window.innerHeight}`
  };
  
  analytics.events.push(event);
  
  // Keep only last 500 events to prevent storage overflow
  if (analytics.events.length > 500) {
    analytics.events = analytics.events.slice(-500);
  }
  
  saveAnalytics(analytics);
  
  // Log in development
  if (process.env.NODE_ENV === 'development') {
    console.log('📊 Analytics:', eventName, eventData);
  }
  
  return event;
};

// Track page view
export const trackPageView = (pageName) => {
  return trackEvent('page_view', { page: pageName });
};

// Track user action
export const trackAction = (action, details = {}) => {
  return trackEvent('user_action', { action, ...details });
};

// Track conversion/goal
export const trackConversion = (conversionType, value = {}) => {
  return trackEvent('conversion', { type: conversionType, ...value });
};

// Track errors
export const trackError = (errorType, errorMessage, context = {}) => {
  return trackEvent('error', { type: errorType, message: errorMessage, ...context });
};

// ==================== SPECIFIC TRACKING FUNCTIONS ====================

// User tracking
export const trackUserRegistration = (role, method) => {
  return trackConversion('registration', { role, method });
};

export const trackUserLogin = (method) => {
  return trackAction('login', { method });
};

// Booking tracking
export const trackBookingStarted = (stylistId, serviceId) => {
  return trackAction('booking_started', { stylist_id: stylistId, service_id: serviceId });
};

export const trackBookingCompleted = (bookingId, amount, paymentMethod) => {
  return trackConversion('booking_completed', { 
    booking_id: bookingId, 
    amount, 
    payment_method: paymentMethod 
  });
};

export const trackBookingAbandoned = (step, reason = '') => {
  return trackAction('booking_abandoned', { step, reason });
};

// Feature usage tracking
export const trackFeatureUsed = (featureName, details = {}) => {
  return trackAction('feature_used', { feature: featureName, ...details });
};

// Search tracking
export const trackSearch = (searchType, query, resultsCount) => {
  return trackAction('search', { type: searchType, query, results_count: resultsCount });
};

// Gallery tracking
export const trackGalleryUpload = (imageCount) => {
  return trackAction('gallery_upload', { image_count: imageCount });
};

// SMS tracking
export const trackSMSSent = (notificationType) => {
  return trackAction('sms_sent', { type: notificationType });
};

// Membership tracking
export const trackMembershipView = (tier) => {
  return trackAction('membership_viewed', { tier });
};

export const trackMembershipUpgrade = (fromTier, toTier) => {
  return trackConversion('membership_upgrade', { from: fromTier, to: toTier });
};

// ==================== ANALYTICS DASHBOARD DATA ====================

// Get summary metrics for dashboard
export const getAnalyticsSummary = () => {
  const analytics = getStoredAnalytics();
  const events = analytics.events;
  
  // Time-based filtering
  const now = new Date();
  const oneDayAgo = new Date(now - 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now - 7 * 24 * 60 * 60 * 1000);
  const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
  
  const eventsToday = events.filter(e => new Date(e.timestamp) > oneDayAgo);
  const eventsWeek = events.filter(e => new Date(e.timestamp) > sevenDaysAgo);
  const eventsMonth = events.filter(e => new Date(e.timestamp) > thirtyDaysAgo);
  
  // Count unique sessions
  const uniqueSessions = new Set(events.map(e => e.session_id)).size;
  const uniqueSessionsToday = new Set(eventsToday.map(e => e.session_id)).size;
  const uniqueSessionsWeek = new Set(eventsWeek.map(e => e.session_id)).size;
  
  // Count page views
  const pageViews = events.filter(e => e.event === 'page_view').length;
  const pageViewsToday = eventsToday.filter(e => e.event === 'page_view').length;
  
  // Count conversions
  const registrations = events.filter(e => e.event === 'conversion' && e.data.type === 'registration').length;
  const bookings = events.filter(e => e.event === 'conversion' && e.data.type === 'booking_completed').length;
  
  // Popular pages
  const pageCounts = {};
  events.filter(e => e.event === 'page_view').forEach(e => {
    const page = e.data.page || e.url;
    pageCounts[page] = (pageCounts[page] || 0) + 1;
  });
  const popularPages = Object.entries(pageCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);
  
  // Feature usage
  const featureCounts = {};
  events.filter(e => e.event === 'user_action' && e.data.feature).forEach(e => {
    featureCounts[e.data.feature] = (featureCounts[e.data.feature] || 0) + 1;
  });
  const popularFeatures = Object.entries(featureCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);
  
  // Device breakdown
  const mobileRegex = /Mobile|Android|iPhone|iPad/i;
  const mobileEvents = events.filter(e => mobileRegex.test(e.user_agent || '')).length;
  const desktopEvents = events.length - mobileEvents;
  
  return {
    overview: {
      totalEvents: events.length,
      totalSessions: uniqueSessions,
      pageViews,
      registrations,
      bookings
    },
    today: {
      events: eventsToday.length,
      sessions: uniqueSessionsToday,
      pageViews: pageViewsToday
    },
    week: {
      events: eventsWeek.length,
      sessions: uniqueSessionsWeek
    },
    month: {
      events: eventsMonth.length
    },
    popularPages,
    popularFeatures,
    devices: {
      mobile: mobileEvents,
      desktop: desktopEvents,
      mobilePercent: events.length > 0 ? Math.round((mobileEvents / events.length) * 100) : 0
    },
    recentEvents: events.slice(-20).reverse()
  };
};

// Export all analytics data (for investor reports)
export const exportAnalyticsData = () => {
  const analytics = getStoredAnalytics();
  const summary = getAnalyticsSummary();
  
  return {
    exportDate: new Date().toISOString(),
    summary,
    rawEvents: analytics.events
  };
};

// Clear analytics (for testing)
export const clearAnalytics = () => {
  localStorage.removeItem(ANALYTICS_KEY);
  sessionStorage.removeItem(SESSION_KEY);
};

// ==================== AUTO-TRACKING SETUP ====================

// Initialize analytics on app load
export const initAnalytics = () => {
  // Track app load
  trackEvent('app_loaded', {
    referrer: document.referrer,
    language: navigator.language
  });
  
  // Track page visibility changes
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackEvent('page_hidden');
    } else {
      trackEvent('page_visible');
    }
  });
  
  // Track before unload
  window.addEventListener('beforeunload', () => {
    trackEvent('page_unload');
  });
  
  console.log('📊 MyHairChair Analytics initialized');
};

export default {
  trackEvent,
  trackPageView,
  trackAction,
  trackConversion,
  trackError,
  trackUserRegistration,
  trackUserLogin,
  trackBookingStarted,
  trackBookingCompleted,
  trackBookingAbandoned,
  trackFeatureUsed,
  trackSearch,
  trackGalleryUpload,
  trackSMSSent,
  trackMembershipView,
  trackMembershipUpgrade,
  getAnalyticsSummary,
  exportAnalyticsData,
  clearAnalytics,
  initAnalytics
};
